import React, { useState, useRef, useCallback, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import WaybackMap from '../components/WaybackMap';
import WaybackTimeline from '../components/WaybackTimeline';
import CompareSwipe from '../components/CompareSwipe';
import SnapshotButton from '../components/SnapshotButton';
import AOIMonitoringPanel from '../components/AOIMonitoringPanel';
import AOICreator from '../components/AOICreator';
import EnhancedChangeDetection from '../components/EnhancedChangeDetection';
import DateRangePicker from '../components/DateRangePicker';
import ReportGenerator from '../components/ReportGenerator';
import GEOINTNotifications from '../components/GEOINTNotifications';
import GEOINTHistory from '../components/GEOINTHistory';
import EntityNavigator from '../components/EntityNavigator';
import IntelFeedPanel from '../components/IntelFeedPanel';
import EntityDetailDrawer from '../components/EntityDetailDrawer';
import CollectionTaskingPanel from '../components/CollectionTaskingPanel';
import TargetPackageBuilder from '../components/TargetPackageBuilder';
import SentinelIntelPanel from '../components/SentinelIntelPanel';
import AOIIntelBrief from '../components/AOIIntelBrief';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

interface Release {
  id: string | number;
  date: string;
  title: string;
  hasLocalChanges: boolean;
  footprint?: {
    type: string;
    coordinates: number[][][] | number[][];
  } | null;
  tileTemplate?: string;
  thumbUrl?: string;
  source?: 'esri' | 'eosda'; // Track source of release
}


/** Get auth token from any of the localStorage keys used across the app. */
const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

const GEOINT_Wayback: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [activeReleaseId, setActiveReleaseId] = useState<string | number | null>(null);
  const [searchLocation, setSearchLocation] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [onlyLocal, setOnlyLocal] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareReleaseA, setCompareReleaseA] = useState<string | number | null>(null);
  const [compareReleaseB, setCompareReleaseB] = useState<string | number | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const [loading, setLoading] = useState(false);
  // showMilitaryBases is driven by the same toggle as showGlobalBases
  const [activeTab, setActiveTab] = useState<'monitoring' | 'analysis' | 'entities' | 'timeline' | 'history' | 'intelligence'>('monitoring');
  const [showAOICreator, setShowAOICreator] = useState(false);
  const [selectedAOI, setSelectedAOI] = useState<any>(null);
  const [changeDetectionJob, setChangeDetectionJob] = useState<any>(null);
  const [drawingMode, setDrawingMode] = useState<'rectangle' | 'polygon' | null>(null);
  const [drawnGeometry, setDrawnGeometry] = useState<any>(null);
  const [aoiRefreshKey, setAoiRefreshKey] = useState(0);
  const [beforeReleaseId, setBeforeReleaseId] = useState<string | number | null>(null);
  const [afterReleaseId, setAfterReleaseId] = useState<string | number | null>(null);
  const [savedAOIs, setSavedAOIs] = useState<any[]>([]);

  // Deep-link params from email alert links (?aoi=&before=&after=)
  const [deepLinkAoiId, setDeepLinkAoiId] = useState<string | null>(null);
  const [deepLinkBefore, setDeepLinkBefore] = useState<string | null>(null);
  const [deepLinkAfter, setDeepLinkAfter] = useState<string | null>(null);

  // Phase 4: Analyst workspace state
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [entityDrawerOpen, setEntityDrawerOpen] = useState(false);
  const [entityPins, setEntityPins] = useState<any[]>([]);
  const [activeBrief, setActiveBrief] = useState<any>(null);
  const [briefDetectionPins, setBriefDetectionPins] = useState<any[]>([]);
  const [briefSatelliteResults, setBriefSatelliteResults] = useState<any>(null);
  const [showTargetPackageBuilder, setShowTargetPackageBuilder] = useState(false);

  const [timelineDateRange, setTimelineDateRange] = useState<number>(180);

  // 3D & Intelligence overlay toggles
  const [enable3D, setEnable3D] = useState(false);
  const [showGlobalBases, setShowGlobalBases] = useState(false);
  const [showConflictZones, setShowConflictZones] = useState(false);
  const [showNuclearSites, setShowNuclearSites] = useState(false);
  const [showChokepoints, setShowChokepoints] = useState(false);

  // Phase 2: Spectral, SAR, FIRMS, Object Detection
  const [spectralIndex, setSpectralIndex] = useState<string | null>(null); // null = RGB, 'ndvi' | 'ndwi' | 'ndbi' | 'nbr'
  const [showFIRMS, setShowFIRMS] = useState(false);
  const [showSAROnly, setShowSAROnly] = useState(false);
  const [objectDetectionResults, setObjectDetectionResults] = useState<any>(null);
  const [isDetectingObjects, setIsDetectingObjects] = useState(false);
  const [liveAircraftResults, setLiveAircraftResults] = useState<any[]>([]);
  const [aircraftSource, setAircraftSource] = useState<string>('');
  const [isFetchingAircraft, setIsFetchingAircraft] = useState(false);
  const [liveVesselResults, setLiveVesselResults] = useState<any[]>([]);
  const [vesselDataSource, setVesselDataSource] = useState<string>('');
  const [isFetchingVessels, setIsFetchingVessels] = useState(false);
  // Map-wide "showcase" layer — live aircraft/vessels around the current view,
  // shown on page load without requiring an AOI, to demonstrate tracking capability.
  const [showcaseAircraft, setShowcaseAircraft] = useState<Array<{ id: string; label: string; type: string; lat: number; lon: number; track?: number | null }>>([]);
  const [showcaseVessels, setShowcaseVessels] = useState<Array<{ id: string; label: string; type: string; lat: number; lon: number; course?: number | null }>>([]);
  const [fr24Usage, setFr24Usage] = useState<{ credits_used: number | null; budget: number; remaining: number | null } | null>(null);
  const showcaseLoadedRef = useRef(false);

  // Full per-aircraft/vessel data (icao24/mmsi -> the enriched flight/vessel dict),
  // populated by both the showcase fetch and the "Check Live Aircraft/Vessels"
  // buttons — used to look up route/photo details when a marker is clicked, since
  // the map layer itself only carries the slim {id,label,type,lat,lon} shape.
  const [aircraftDetails, setAircraftDetails] = useState<Record<string, any>>({});
  const [vesselDetails, setVesselDetails] = useState<Record<string, any>>({});
  const [selectedAircraftId, setSelectedAircraftId] = useState<string | null>(null);
  const [selectedVesselId, setSelectedVesselId] = useState<string | null>(null);
  const [selectedAircraftPhoto, setSelectedAircraftPhoto] = useState<{ thumbnail_url?: string; photographer?: string; credit_link?: string } | null>(null);
  const [isFetchingAircraftPhoto, setIsFetchingAircraftPhoto] = useState(false);

  const selectedAircraft = selectedAircraftId ? aircraftDetails[selectedAircraftId] : null;
  const selectedVessel = selectedVesselId ? vesselDetails[selectedVesselId] : null;

  const selectedFlightPath = React.useMemo(() => {
    if (!selectedAircraft || selectedAircraft.lat == null || selectedAircraft.lon == null) return null;
    const origin = selectedAircraft.origin_lat != null
      ? { lat: selectedAircraft.origin_lat, lon: selectedAircraft.origin_lon, name: selectedAircraft.origin_name }
      : null;
    const destination = selectedAircraft.dest_lat != null
      ? { lat: selectedAircraft.dest_lat, lon: selectedAircraft.dest_lon, name: selectedAircraft.dest_name }
      : null;
    if (!origin && !destination) return null;
    return { origin, current: { lat: selectedAircraft.lat, lon: selectedAircraft.lon }, destination };
  }, [selectedAircraft]);

  // Fetch the aircraft photo (planespotters.net, cached server-side) whenever the
  // selected aircraft changes.
  useEffect(() => {
    if (!selectedAircraft?.icao24) { setSelectedAircraftPhoto(null); return; }
    let cancelled = false;
    setIsFetchingAircraftPhoto(true);
    setSelectedAircraftPhoto(null);
    const token = getAuthToken();
    const params = new URLSearchParams({ icao24: selectedAircraft.icao24 });
    if (selectedAircraft.registration) params.set('registration', selectedAircraft.registration);
    fetch(`/api/geoint/aircraft-photo?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(photo => {
        if (cancelled) return;
        setSelectedAircraftPhoto(photo?.thumbnail_url ? photo : null);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsFetchingAircraftPhoto(false); });
    return () => { cancelled = true; };
  }, [selectedAircraft?.icao24, selectedAircraft?.registration]);

  const mapRef = useRef<maplibregl.Map>(null);

  // Fetch a bounded snapshot of live aircraft + vessels around the map's current
  // view and render them on the base map — fires once when the map finishes
  // loading, before any AOI is selected. Both endpoints are cached server-side
  // (~3 min TTL) and capped, so this is safe to run on every page load without
  // repeatedly spending FR24/VesselAPI credits. See docs/flightradar plan.md.
  const loadShowcaseTraffic = useCallback(async () => {
    if (showcaseLoadedRef.current || !mapRef.current) return;
    showcaseLoadedRef.current = true;
    const bounds = mapRef.current.getBounds();
    const token = getAuthToken();
    const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const resp = await fetch(
        `/api/geoint/aircraft?lamin=${bounds.getSouth()}&lomin=${bounds.getWest()}&lamax=${bounds.getNorth()}&lomax=${bounds.getEast()}&limit=150`,
        { headers: authHeader }
      );
      if (resp.ok) {
        const data = await resp.json();
        setAircraftSource(data.source || '');
        const flights = data.flights || [];
        setShowcaseAircraft(flights.map((f: any) => ({
          id: f.icao24,
          label: f.callsign || f.icao24,
          type: f.type || '',
          lat: f.lat,
          lon: f.lon,
          track: f.track,
        })));
        setAircraftDetails(prev => ({ ...prev, ...Object.fromEntries(flights.map((f: any) => [f.icao24, f])) }));
      }
    } catch { /* showcase layer is best-effort */ }

    try {
      const resp = await fetch('/api/maritime/vessels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          south: bounds.getSouth(), west: bounds.getWest(),
          north: bounds.getNorth(), east: bounds.getEast(),
          limit: 50,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const vessels = (data.vessels || []).filter((v: any) => v.latitude != null && v.longitude != null);
        setShowcaseVessels(vessels.map((v: any) => ({
          id: v.mmsi,
          label: v.name || v.mmsi,
          type: v.type || '',
          lat: v.latitude,
          lon: v.longitude,
          course: v.course,
        })));
        setVesselDetails(prev => ({ ...prev, ...Object.fromEntries(vessels.map((v: any) => [v.mmsi, v])) }));
      }
    } catch { /* showcase layer is best-effort */ }

    try {
      const resp = await fetch('/api/geoint/fr24-usage', { headers: authHeader });
      if (resp.ok) setFr24Usage(await resp.json());
    } catch { /* usage indicator is best-effort */ }
  }, []);

  const computeAOIBbox = (aoi: any): { south: number; west: number; north: number; east: number } | null => {
    if (!aoi?.geometry) return null;
    const coords = aoi.geometry.type === 'Polygon' ? aoi.geometry.coordinates[0] : [aoi.geometry.coordinates];
    const lons = (coords as number[][]).map((c: number[]) => c[0]);
    const lats = (coords as number[][]).map((c: number[]) => c[1]);
    return { west: Math.min(...lons), east: Math.max(...lons), south: Math.min(...lats), north: Math.max(...lats) };
  };

  // Shared by the "Check Live Aircraft" button and by clicking an
  // aircraft_activity notification (auto-triggers this for the notification's AOI).
  const checkLiveAircraftForAOI = useCallback(async (aoi: any) => {
    const bbox = computeAOIBbox(aoi);
    if (!bbox) return;
    setIsFetchingAircraft(true);
    setLiveAircraftResults([]);
    try {
      const token = getAuthToken();
      const resp = await fetch(
        `/api/geoint/aircraft?lamin=${bbox.south}&lomin=${bbox.west}&lamax=${bbox.north}&lomax=${bbox.east}&limit=200`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (resp.ok) {
        const data = await resp.json();
        setAircraftSource(data.source || '');
        const flights = (data.flights || []).filter((f: any) => f.lat != null && f.lon != null);
        setLiveAircraftResults(flights);
        setAircraftDetails(prev => ({ ...prev, ...Object.fromEntries(flights.map((f: any) => [f.icao24, f])) }));
        // WaybackMap's marker layer reads showcaseAircraft, not liveAircraftResults —
        // update it too so an AOI-triggered check actually shows up on the map,
        // not just in the sidebar list.
        setShowcaseAircraft(flights.map((f: any) => ({
          id: f.icao24, label: f.callsign || f.icao24, type: f.type || '', lat: f.lat, lon: f.lon, track: f.track,
        })));
        fetch('/api/geoint/fr24-usage', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
          .then(r => r.ok ? r.json() : null).then(u => u && setFr24Usage(u)).catch(() => {});
      }
    } catch { /* silent */ } finally {
      setIsFetchingAircraft(false);
    }
  }, []);

  // Shared by the "Check Live Vessels" button and by clicking a
  // vessel_activity notification.
  const checkLiveVesselsForAOI = useCallback(async (aoi: any) => {
    const bbox = computeAOIBbox(aoi);
    if (!bbox) return;
    setIsFetchingVessels(true);
    setLiveVesselResults([]);
    setVesselDataSource('');
    try {
      const token = getAuthToken();
      const resp = await fetch('/api/maritime/vessels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ south: bbox.south, west: bbox.west, north: bbox.north, east: bbox.east, limit: 50 }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const vessels = data.vessels || [];
        setLiveVesselResults(vessels);
        setVesselDataSource(data.data_source || '');
        setVesselDetails(prev => ({ ...prev, ...Object.fromEntries(vessels.map((v: any) => [v.mmsi, v])) }));
        // Same fix as aircraft — WaybackMap's marker layer reads showcaseVessels.
        setShowcaseVessels(vessels
          .filter((v: any) => v.latitude != null && v.longitude != null)
          .map((v: any) => ({ id: v.mmsi, label: v.name || v.mmsi, type: v.type || '', lat: v.latitude, lon: v.longitude, course: v.course })));
      }
    } catch { /* silent */ } finally {
      setIsFetchingVessels(false);
    }
  }, []);

  // Comprehensive military bases from India, China, and Pakistan
  const militaryBases = [
    // Indian Military Bases
    { id: 1, name: "Siachen Glacier Base", coordinates: [77.6915, 35.4212] as [number, number], type: "Army", country: "India", info: "World's highest battlefield, strategic position on Siachen Glacier" },
    { id: 2, name: "Leh Air Force Station", coordinates: [77.6915, 34.1351] as [number, number], type: "Air Force", country: "India", info: "Strategic air base in Ladakh, high-altitude operations" },
    { id: 3, name: "Srinagar Air Force Station", coordinates: [74.7973, 34.0837] as [number, number], type: "Air Force", country: "India", info: "Primary air base in Kashmir, counter-terrorism operations" },
    { id: 4, name: "Pathankot Air Force Station", coordinates: [75.6371, 32.2743] as [number, number], type: "Air Force", country: "India", info: "Strategic air base near Pakistan border" },
    { id: 5, name: "Amritsar Army Base", coordinates: [74.8570, 31.6340] as [number, number], type: "Army", country: "India", info: "Major army base near Pakistan border" },
    { id: 6, name: "Jaisalmer Air Force Station", coordinates: [70.9027, 26.9157] as [number, number], type: "Air Force", country: "India", info: "Desert air base, strategic for western operations" },
    { id: 7, name: "Bikaner Army Base", coordinates: [73.3149, 28.0229] as [number, number], type: "Army", country: "India", info: "Desert warfare training center" },
    { id: 8, name: "Jodhpur Air Force Station", coordinates: [73.0089, 26.2389] as [number, number], type: "Air Force", country: "India", info: "Strategic air base in Rajasthan" },
    { id: 9, name: "Uttarlai Air Force Station", coordinates: [73.0089, 25.2389] as [number, number], type: "Air Force", country: "India", info: "Forward air base near Pakistan border" },
    { id: 10, name: "Barmer Army Base", coordinates: [71.3924, 25.7471] as [number, number], type: "Army", country: "India", info: "Desert warfare and border security" },
    { id: 11, name: "Ganganagar Army Base", coordinates: [73.8719, 29.9038] as [number, number], type: "Army", country: "India", info: "Border security operations" },
    { id: 12, name: "Fazilka Army Base", coordinates: [74.0283, 30.4021] as [number, number], type: "Army", country: "India", info: "Strategic border position" },
    
    // Chinese Military Bases
    { id: 13, name: "Hotan Air Base", coordinates: [79.9253, 37.1142] as [number, number], type: "Air Force", country: "China", info: "PLA Air Force base in Xinjiang, near Indian border" },
    { id: 14, name: "Kashgar Air Base", coordinates: [75.9938, 39.4704] as [number, number], type: "Air Force", country: "China", info: "Major PLA air base in western Xinjiang" },
    { id: 15, name: "Lhasa Gonggar Airport", coordinates: [90.9129, 29.2975] as [number, number], type: "Air Force", country: "China", info: "PLA air base in Tibet, dual-use civilian-military" },
    { id: 16, name: "Ngari Gunsa Airport", coordinates: [80.0544, 32.1003] as [number, number], type: "Air Force", country: "China", info: "PLA air base in western Tibet, near Indian border" },
    { id: 17, name: "Shigatse Peace Airport", coordinates: [88.8858, 29.3517] as [number, number], type: "Air Force", country: "China", info: "PLA air base in Tibet, strategic location" },
    { id: 18, name: "Aksai Chin Base", coordinates: [78.5, 35.0] as [number, number], type: "Army", country: "China", info: "PLA border defense base in disputed territory" },
    
    // Pakistani Military Bases
    { id: 19, name: "Skardu Air Base", coordinates: [75.6333, 35.3167] as [number, number], type: "Air Force", country: "Pakistan", info: "PAF base in Gilgit-Baltistan, near Indian border" },
    { id: 20, name: "Gilgit Air Base", coordinates: [74.3081, 35.9211] as [number, number], type: "Air Force", country: "Pakistan", info: "PAF base in northern Pakistan" },
    { id: 21, name: "Sialkot Air Base", coordinates: [74.5407, 32.4945] as [number, number], type: "Air Force", country: "Pakistan", info: "PAF base near Indian border" },
    { id: 22, name: "Lahore Air Base", coordinates: [74.3587, 31.5204] as [number, number], type: "Air Force", country: "Pakistan", info: "Major PAF base in Punjab" },
    { id: 23, name: "Karachi Air Base", coordinates: [67.0011, 24.8607] as [number, number], type: "Air Force", country: "Pakistan", info: "PAF Southern Air Command headquarters" },
    { id: 24, name: "Quetta Air Base", coordinates: [66.9750, 30.1798] as [number, number], type: "Air Force", country: "Pakistan", info: "PAF base in Balochistan" },
    { id: 25, name: "Rawalpindi Army Base", coordinates: [73.0479, 33.5651] as [number, number], type: "Army", country: "Pakistan", info: "Pakistan Army headquarters" },
    { id: 26, name: "Lahore Army Base", coordinates: [74.3587, 31.5204] as [number, number], type: "Army", country: "Pakistan", info: "Major army base in Punjab" },
    { id: 27, name: "Karachi Naval Base", coordinates: [67.0011, 24.8607] as [number, number], type: "Navy", country: "Pakistan", info: "Pakistan Navy headquarters" },
    { id: 28, name: "Gwadar Naval Base", coordinates: [62.3254, 25.1216] as [number, number], type: "Navy", country: "Pakistan", info: "Strategic naval base on Arabian Sea" }
  ];

  const fetchReleasesForLocation = useCallback(async (coordinates: [number, number], dateRangeDays = 180) => {
    try {
      setLoading(true);
      const [lon, lat] = coordinates;
      // Use the actual AOI polygon if available, else fall back to a small bbox
      let areaGeom = selectedAOI?.geometry;
      if (!areaGeom) {
        areaGeom = {
          type: "Polygon",
          coordinates: [[
            [lon - 0.05, lat - 0.05],
            [lon + 0.05, lat - 0.05],
            [lon + 0.05, lat + 0.05],
            [lon - 0.05, lat + 0.05],
            [lon - 0.05, lat - 0.05]
          ]]
        };
      }

      const aoiParam = encodeURIComponent(JSON.stringify(areaGeom));
      const today = new Date().toISOString();
      const response = await fetch(
        `/api/geoint/wayback/releases?aoi=${aoiParam}&onlyLocal=${onlyLocal}&targetDate=${today}&dateRange=${dateRangeDays}&useEOSDAFallback=true`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch releases for location');
      }

      const data = await response.json();
      setReleases(data);
    } catch (error) {
      console.error('Failed to fetch releases for location', error);
    } finally {
      setLoading(false);
    }
  }, [onlyLocal, selectedAOI?.geometry]);

  const handleLocationSearch = useCallback(async (location: string) => {
    if (!location.trim()) return;

    try {
      setLoading(true);

      // 1) Check hardcoded military bases first (Nominatim often misses these)
      const query = location.trim().toLowerCase();
      const baseMatch = militaryBases.find(b =>
        query.includes(b.name.toLowerCase()) || b.name.toLowerCase().includes(query)
      );

      let coordinates: [number, number];
      let displayName: string;

      if (baseMatch) {
        coordinates = baseMatch.coordinates;
        displayName = `${baseMatch.name}, ${baseMatch.country} (${baseMatch.type})`;
      } else {
        // 2) Try Nominatim geocoding
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=5`);
        const results = await response.json();

        if (results.length > 0) {
          const result = results[0];
          coordinates = [parseFloat(result.lon), parseFloat(result.lat)];
          displayName = result.display_name;
        } else {
          // 3) Try partial match — strip common suffixes and retry
          const simplified = location.replace(/\b(air base|air force|naval base|army base|station|airport|military)\b/gi, '').trim();
          if (simplified !== location.trim()) {
            const retryResp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(simplified)}&limit=1`);
            const retryResults = await retryResp.json();
            if (retryResults.length > 0) {
              coordinates = [parseFloat(retryResults[0].lon), parseFloat(retryResults[0].lat)];
              displayName = `${location} (near ${retryResults[0].display_name})`;
            } else {
              alert('Location not found. Try a city name, coordinates, or a known military base name.');
              return;
            }
          } else {
            alert('Location not found. Try a city name, coordinates, or a known military base name.');
            return;
          }
        }
      }

      setSelectedLocation({
        name: displayName,
        coordinates: coordinates,
      });

      // Fetch releases for this location
      await fetchReleasesForLocation(coordinates);

      // Record search in history (fire-and-forget)
      const token = getAuthToken();
      fetch('/api/geoint/wayback/history/record-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          location: displayName,
          coordinates,
        }),
      }).catch(() => {});
    } catch (error) {
      console.error('Location search failed:', error);
      alert('Location search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchReleasesForLocation]);

  const fetchReleases = async (onlyLocalChanges = false, startDate?: Date | null, endDate?: Date | null, targetDate?: Date | null) => {
    try {
      setLoading(true);
      let url = `/api/geoint/wayback/releases?onlyLocal=${onlyLocalChanges}&useEOSDAFallback=true`;
      
      // Add AOI if available for EOSDA fallback
      if (selectedAOI?.geometry) {
        const aoiParam = encodeURIComponent(JSON.stringify(selectedAOI.geometry));
        url += `&aoi=${aoiParam}`;
      }
      
      // If no dates provided, default to last 30 days
      if (!startDate && !endDate && !targetDate) {
        const today = new Date();
        url += `&targetDate=${today.toISOString()}&dateRange=30`;
      } else if (targetDate) {
        // Fetch nearby dates around target date (±15 days)
        url += `&targetDate=${targetDate.toISOString()}&dateRange=15`;
      } else {
        if (startDate) {
          url += `&startDate=${startDate.toISOString()}`;
        }
        if (endDate) {
          url += `&endDate=${endDate.toISOString()}`;
        }
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setReleases(data);
      }
    } catch (error) {
      console.error('Failed to fetch releases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    fetchReleases(onlyLocal, start, end);
  };

  const handleAOICreate = async (aoiData: any) => {
    try {
      const requestData = {
        name: aoiData.name,
        description: aoiData.description,
        geometry: {
          type: aoiData.geometry.type,
          coordinates: aoiData.geometry.coordinates
        },
        monitoring_enabled: true
      };

      const token = getAuthToken();
      const response = await fetch('/api/geoint/wayback/aois', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setShowAOICreator(false);
        setDrawingMode(null);
        setDrawnGeometry(null);
        setAoiRefreshKey(prev => prev + 1);
        if (result.aoi_id || result.id) {
          setSelectedAOI(result);
        }
        // Immediately refresh saved AOIs to display on map
        await fetchSavedAOIs();
      } else {
        const errorText = await response.text();
        console.error('Failed to create AOI:', response.status, errorText);
        alert(`Failed to create AOI: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to create AOI:', error);
      alert(`Error creating AOI: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };


  const handleReleaseSelect = (releaseId: string | number) => {
    setActiveReleaseId(releaseId);
  };


  const handleCompareToggle = () => {
    if (!showCompare) {
      // Sync: use before/after from Analysis tab if set, otherwise use active release
      setCompareReleaseA(beforeReleaseId || activeReleaseId);
      setCompareReleaseB(afterReleaseId || null);
    }
    setShowCompare(!showCompare);
  };

  const handleSnapshotSaved = (_snapshotId: string) => {
    // Snapshot is persisted server-side; no further action needed here
  };

  const fetchSavedAOIs = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch('/api/geoint/wayback/aois', {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (response.ok) {
        const data = await response.json();
        const aois = data.aois || data || [];
        setSavedAOIs(aois);
      } else {
        console.error('Failed to fetch AOIs:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch saved AOIs:', error);
    }
  };

  // Fetch entity pins when AOI is selected
  useEffect(() => {
    if (!selectedAOI?.geometry) {
      setEntityPins([]);
      return;
    }
    const geom = selectedAOI.geometry;
    let center: { lat: number; lon: number } | null = null;
    if (geom.type === 'Point' && geom.coordinates) {
      center = { lat: geom.coordinates[1], lon: geom.coordinates[0] };
    } else {
      const cs = geom.coordinates?.[0] || [];
      if (cs.length > 0) {
        center = {
          lat: cs.reduce((a: number, c: number[]) => a + c[1], 0) / cs.length,
          lon: cs.reduce((a: number, c: number[]) => a + c[0], 0) / cs.length,
        };
      }
    }
    if (!center) return;

    const token = getAuthToken();
    // Use entities/search (queries ENTITIES index by last_location geo_point) instead of
    // correlate/location (queries OBSERVATIONS index) — more reliable, no time-window restriction
    fetch('/api/entities/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        location: { lat: center.lat, lon: center.lon },
        radius_km: 50,
        size: 100,
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.entities) {
          const pins = (data.entities as any[])
            .filter((e: any) => e.last_location?.lat != null && e.last_location?.lon != null)
            .map((e: any) => ({
              entity_id: e.entity_id,
              name: e.name,
              entity_type: e.entity_type,
              lat: e.last_location.lat,
              lon: e.last_location.lon,
              priority: e.priority,
              is_watchlisted: e.is_watchlisted,
              observation_count: e.observation_count,
            }));
          setEntityPins(pins);
        }
      })
      .catch(() => setEntityPins([]));
  }, [selectedAOI?.id, selectedAOI?.aoi_id]);

  // Entity selection handlers — fly to entity location with 3D view
  const flyToEntity = useCallback((entity: any) => {
    if (!entity?.last_location || !mapRef.current) return;
    const { lat, lon } = entity.last_location;
    // Enable 3D terrain for immersive view
    setEnable3D(true);
    // Fly to entity with cinematic animation
    mapRef.current.flyTo({
      center: [lon, lat],
      zoom: 16,
      pitch: 55,
      bearing: -30,
      duration: 2000,
      essential: true,
    });
  }, []);

  // When a change-detection entity is selected, load its job so the map overlay shows
  const loadEntityChangeDetectionJob = useCallback((entity: any) => {
    const jobId = entity?.attributes?.job_id;
    if (!jobId) return;
    const token = getAuthToken();
    fetch(`/api/geoint/wayback/change-detection/${jobId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(job => { if (job?.metrics) setChangeDetectionJob(job); })
      .catch(() => {});
  }, []);

  const handleEntitySelect = useCallback((entity: any) => {
    setSelectedEntity(entity);
    setEntityDrawerOpen(true);
    flyToEntity(entity);
    loadEntityChangeDetectionJob(entity);
  }, [flyToEntity, loadEntityChangeDetectionJob]);

  const handleEntityPinClick = useCallback((entityId: string) => {
    const token = getAuthToken();
    fetch(`/api/entities/${entityId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(entity => {
        if (entity) {
          setSelectedEntity(entity);
          setEntityDrawerOpen(true);
          flyToEntity(entity);
          loadEntityChangeDetectionJob(entity);
        }
      })
      .catch(() => {});
  }, [flyToEntity, loadEntityChangeDetectionJob]);

  // Load initial releases - use selectedLocation if available, otherwise fetch general releases
  React.useEffect(() => {
    if (selectedLocation?.coordinates) {
      // If a location is selected, fetch releases for that location
      fetchReleasesForLocation(selectedLocation.coordinates);
    } else {
      // Otherwise, fetch general releases for today/yesterday
      fetchReleases(onlyLocal, null, null, new Date());
    }
  }, [onlyLocal, selectedLocation, fetchReleasesForLocation]);

  // Fetch releases when AOI is selected (use AOI center as location)
  React.useEffect(() => {
    if (!selectedAOI?.geometry?.coordinates) return;
    const coords = selectedAOI.geometry.coordinates;
    const ring = (coords[0] as number[][]) || (coords as unknown as number[][]);
    if (!ring || ring.length === 0) return;
    const lons = ring.map((c: number[]) => c[0]);
    const lats = ring.map((c: number[]) => c[1]);
    const centerLon = lons.reduce((a: number, b: number) => a + b, 0) / lons.length;
    const centerLat = lats.reduce((a: number, b: number) => a + b, 0) / lats.length;
    // Auto-switch to timeline tab so the user sees the fetch happening
    setActiveTab('timeline');
    fetchReleasesForLocation([centerLon, centerLat], 180);
  }, [selectedAOI?.id, selectedAOI?.aoi_id, (selectedAOI as any)?._id]);

  // Load saved AOIs on mount
  React.useEffect(() => {
    fetchSavedAOIs();
  }, []);

  // Parse deep-link query params on mount (from email alert "Compare" links)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aoi = params.get('aoi');
    const before = params.get('before');
    const after = params.get('after');
    if (aoi) setDeepLinkAoiId(aoi);
    if (before) setDeepLinkBefore(before);
    if (after) setDeepLinkAfter(after);
    // Pre-switch to analysis tab so before/after selectors are visible
    if (before || after) setActiveTab('analysis');
  }, []);

  // Apply deep-link AOI once savedAOIs has loaded
  React.useEffect(() => {
    if (!deepLinkAoiId || savedAOIs.length === 0) return;
    const aoi = savedAOIs.find((a: any) => (a.id || a.aoi_id) === deepLinkAoiId);
    if (aoi) {
      setSelectedAOI(aoi);
      setDeepLinkAoiId(null);
    }
  }, [savedAOIs, deepLinkAoiId]);

  // Apply deep-link before/after releases once releases have loaded
  React.useEffect(() => {
    if (releases.length === 0) return;
    if (deepLinkBefore) {
      const rel = releases.find((r: Release) => String(r.id) === deepLinkBefore);
      if (rel) {
        setBeforeReleaseId(rel.id);
        setDeepLinkBefore(null);
      }
    }
    if (deepLinkAfter) {
      const rel = releases.find((r: Release) => String(r.id) === deepLinkAfter);
      if (rel) {
        setAfterReleaseId(rel.id);
        setActiveReleaseId(rel.id);
        setDeepLinkAfter(null);
      }
    }
  }, [releases, deepLinkBefore, deepLinkAfter]);

  return (
    <div className="-m-6 flex h-[calc(100vh-64px)] bg-background overflow-hidden">
      {/* Left Panel - Enhanced with Tabs */}
      <div className="w-96 bg-card border-r border-border flex flex-col">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <h1 className="text-sm font-bold text-foreground bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent leading-tight">
            Sycek Intelligence Geoint
          </h1>
          <span className="text-xs text-muted-foreground hidden xl:block">— Satellite GEOINT</span>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="mx-3 mt-2 grid w-auto grid-cols-6">
            <TabsTrigger value="monitoring">Monitor</TabsTrigger>
            <TabsTrigger value="analysis">Analysis</TabsTrigger>
            <TabsTrigger value="entities">Entities</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="intelligence" className="text-green-400 data-[state=active]:text-green-300">Intel</TabsTrigger>
          </TabsList>

          <TabsContent value="monitoring" className="data-[state=active]:flex-1 min-h-0 overflow-y-auto p-3 space-y-2.5 mt-0">
            {/* Location Search — compact inline row */}
            <div className="flex gap-1.5">
              <input
                type="text"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                placeholder="Search location..."
                className="flex-1 px-2.5 py-1.5 bg-background border border-border rounded text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary text-xs"
                onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch(searchLocation)}
              />
              <button
                onClick={() => handleLocationSearch(searchLocation)}
                disabled={loading || !searchLocation.trim()}
                className="px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:bg-muted text-primary-foreground rounded text-xs font-medium transition-colors shrink-0"
              >
                {loading ? '...' : 'Go'}
              </button>
            </div>

            {selectedLocation && (
              <div className="px-2.5 py-1.5 bg-muted rounded text-xs">
                <span className="text-foreground font-medium truncate block">{selectedLocation.name}</span>
                <span className="text-muted-foreground">
                  {selectedLocation.coordinates[1].toFixed(4)}, {selectedLocation.coordinates[0].toFixed(4)}
                </span>
              </div>
            )}

            <DateRangePicker onDateSelect={handleDateRangeChange} />

            {/* AOI Monitoring */}
            {showAOICreator ? (
              <AOICreator
                onSave={handleAOICreate}
                onCancel={() => {
                  setShowAOICreator(false);
                  setDrawingMode(null);
                  setDrawnGeometry(null);
                }}
                mapRef={mapRef}
                onDrawingModeChange={setDrawingMode}
                drawnGeometry={drawnGeometry}
              />
            ) : (
              <AOIMonitoringPanel
                key={aoiRefreshKey}
                onAOISelect={(aoi) => {
                  setSelectedAOI(aoi);
                  // Refresh saved AOIs to update map display
                  fetchSavedAOIs();
                  // Zoom to AOI on map
                  if (aoi.geometry && mapRef.current) {
                    // Handle both number[][] and number[][][] formats
                    let coords: number[][] = [];
                    const firstCoord = aoi.geometry.coordinates[0];
                    if (Array.isArray(firstCoord)) {
                      if (Array.isArray(firstCoord[0])) {
                        // Check if it's number[][][] format (nested array)
                        if (typeof firstCoord[0][0] === 'number') {
                          // number[][][] format - take first ring
                          coords = firstCoord as unknown as number[][];
                        }
                      } else if (typeof firstCoord[0] === 'number') {
                        // number[][] format
                        coords = aoi.geometry.coordinates as unknown as number[][];
                      }
                    }
                    if (coords && coords.length > 0) {
                      const lngs: number[] = [];
                      const lats: number[] = [];
                      for (const coord of coords) {
                        if (Array.isArray(coord) && coord.length >= 2) {
                          lngs.push(coord[0]);
                          lats.push(coord[1]);
                        }
                      }
                      if (lngs.length > 0 && lats.length > 0) {
                        const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                        mapRef.current.flyTo({
                          center: [centerLng, centerLat],
                          zoom: 12
                        });
                      }
                    }
                  }
                }}
                onAOICreate={() => setShowAOICreator(true)}
              />
            )}

            <div className="flex items-center space-x-2 pt-2 border-t border-border">
              <input
                type="checkbox"
                id="onlyLocal"
                checked={onlyLocal}
                onChange={(e) => setOnlyLocal(e.target.checked)}
                className="rounded border-border"
              />
              <label htmlFor="onlyLocal" className="text-sm text-muted-foreground">
                Only versions with local changes
              </label>
            </div>

            {/* Collection Tasking (Phase 4C) */}
            <CollectionTaskingPanel selectedAOI={selectedAOI} />
          </TabsContent>

          <TabsContent value="analysis" className="data-[state=active]:flex-1 min-h-0 overflow-y-auto p-4 space-y-4 mt-0">
            <EnhancedChangeDetection
              beforeRelease={beforeReleaseId ? releases.find(r => r.id === beforeReleaseId) : undefined}
              afterRelease={afterReleaseId ? releases.find(r => r.id === afterReleaseId) : undefined}
              aoi={selectedAOI}
              onJobComplete={setChangeDetectionJob}
              onBeforeSelect={(releaseId: string | number | null) => {
                setBeforeReleaseId(releaseId);
                if (releaseId) setActiveReleaseId(releaseId);
              }}
              onAfterSelect={(releaseId: string | number | null) => {
                setAfterReleaseId(releaseId);
                if (releaseId) setActiveReleaseId(releaseId);
              }}
            />
            
            <ReportGenerator
              beforeImage={beforeReleaseId ? `/api/tiles/wayback/${beforeReleaseId}/4/8/5.jpg` : undefined}
              afterImage={afterReleaseId ? `/api/tiles/wayback/${afterReleaseId}/4/8/5.jpg` : undefined}
              changeDetectionData={changeDetectionJob}
              aoi={selectedAOI}
              beforeDate={beforeReleaseId ? releases.find(r => r.id === beforeReleaseId)?.date : undefined}
              afterDate={afterReleaseId ? releases.find(r => r.id === afterReleaseId)?.date : undefined}
              beforeReleaseId={beforeReleaseId ?? undefined}
              afterReleaseId={afterReleaseId ?? undefined}
              locationName={selectedLocation?.name}
            />

            {/* Phase 2: ML Object Detection */}
            <div className="border border-border rounded-lg p-3 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">ML Object Detection</h3>
              <p className="text-xs text-muted-foreground">
                Detect vehicles, vessels, aircraft, and structures using YOLO satellite ML.
              </p>
              <button
                onClick={async () => {
                  const releaseId = activeReleaseId || afterReleaseId || beforeReleaseId;
                  if (!releaseId || !selectedAOI?.geometry) return;
                  const token = getAuthToken();
                  setIsDetectingObjects(true);
                  setObjectDetectionResults(null);
                  try {
                    const resp = await fetch('/api/geoint/detect/aoi', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({
                        view_id: String(releaseId),
                        aoi: selectedAOI.geometry,
                        zoom: 16,
                        confidence_threshold: 0.15,
                        tile_source: typeof releaseId === 'string' && (releaseId.startsWith('eosda_') || releaseId.startsWith('copernicus_')) ? 'eosda' : 'wayback',
                      }),
                    });
                    if (resp.ok) {
                      const data = await resp.json();
                      setObjectDetectionResults(data);
                      // Auto-push detected objects (vessels, aircraft, vehicles) to entity ontology
                      if (data.job_id && data.total_objects > 0) {
                        fetch(`/api/geoint/detect/results/${data.job_id}/push-entities`, {
                          method: 'POST',
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                        }).catch(() => {/* non-blocking */});
                      }
                    } else {
                      setObjectDetectionResults({ error: `Detection failed (${resp.status})` });
                    }
                  } catch (err) {
                    console.error('Object detection failed:', err);
                    setObjectDetectionResults({ error: 'Network error — check API connection' });
                  } finally {
                    setIsDetectingObjects(false);
                  }
                }}
                disabled={isDetectingObjects || !(activeReleaseId || afterReleaseId || beforeReleaseId) || !selectedAOI}
                className="w-full px-3 py-2 text-xs font-medium rounded bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isDetectingObjects ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                    Scanning tiles... this may take a minute
                  </>
                ) : 'Run Detection on AOI'}
              </button>

              {isDetectingObjects && (
                <div className="space-y-1.5">
                  <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                    <div className="bg-cyan-500 h-1.5 rounded-full animate-pulse" style={{ width: '60%' }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center">
                    Fetching tiles and running YOLO inference on each...
                  </p>
                </div>
              )}

              {objectDetectionResults?.error && (
                <div className="text-xs text-red-400 p-2 border border-red-800 rounded bg-red-950/20">
                  {objectDetectionResults.error}
                </div>
              )}

              {objectDetectionResults && !objectDetectionResults.error && (
                <div className="text-xs space-y-1 pt-2 border-t border-border">
                  <div className="text-foreground font-medium">
                    {objectDetectionResults.total_objects} objects detected across {objectDetectionResults.tiles_analyzed} tiles
                  </div>
                  {Object.entries(objectDetectionResults.objects_by_class || {}).map(([cls, count]) => (
                    <div key={cls} className="text-muted-foreground flex justify-between">
                      <span className="capitalize">{cls}</span>
                      <span className="text-cyan-400">{String(count)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Live Aircraft — official FR24 API (budget-guarded) / OpenSky fallback, for current AOI bbox */}
              <div className="pt-2 border-t border-border space-y-2">
                <button
                  onClick={() => checkLiveAircraftForAOI(selectedAOI)}
                  disabled={isFetchingAircraft || !selectedAOI}
                  className="w-full px-3 py-2 text-xs font-medium rounded bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {isFetchingAircraft ? 'Querying Sky...' : 'Check Live Aircraft'}
                </button>
                {liveAircraftResults.length > 0 && (
                  <div className="text-xs space-y-1 pt-1">
                    <div className="text-foreground font-medium flex justify-between">
                      <span>{liveAircraftResults.length} aircraft in AOI</span>
                      {aircraftSource && <span className="text-muted-foreground">{aircraftSource}</span>}
                    </div>
                    {liveAircraftResults.slice(0, 8).map((f: any, i: number) => (
                      <div
                        key={i}
                        onClick={() => setSelectedAircraftId(f.icao24)}
                        className={`flex justify-between cursor-pointer rounded px-1 -mx-1 hover:bg-muted/50 ${
                          selectedAircraftId === f.icao24 ? 'bg-orange-500/10 text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        <span className="text-orange-400">{(f.callsign || f.icao24 || '?').trim()}</span>
                        <span>
                          {f.type || f.origin_country || '?'} · {f.on_ground ? 'ground' : `${f.alt ? Math.round(f.alt) + 'ft' : '?'}`}
                          {f.origin_iata && f.dest_iata ? ` · ${f.origin_iata}→${f.dest_iata}` : ''}
                        </span>
                      </div>
                    ))}
                    {liveAircraftResults.length > 8 && (
                      <div className="text-muted-foreground">+{liveAircraftResults.length - 8} more</div>
                    )}
                  </div>
                )}
                {!isFetchingAircraft && liveAircraftResults.length === 0 && liveAircraftResults !== null && (
                  <p className="text-[10px] text-muted-foreground">No aircraft currently tracked in this AOI.</p>
                )}
                {fr24Usage && fr24Usage.credits_used != null && (
                  <p className="text-[10px] text-muted-foreground">
                    FR24 credits this month: {fr24Usage.credits_used.toLocaleString()} / {fr24Usage.budget.toLocaleString()}
                  </p>
                )}
              </div>

              {/* Live Vessels — GFW / AISStream query for current AOI bbox */}
              <div className="pt-2 border-t border-border space-y-2">
                <button
                  onClick={() => checkLiveVesselsForAOI(selectedAOI)}
                  disabled={isFetchingVessels || !selectedAOI}
                  className="w-full px-3 py-2 text-xs font-medium rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {isFetchingVessels ? 'Querying Sea...' : 'Check Live Vessels'}
                </button>
                {liveVesselResults.length > 0 && (
                  <div className="text-xs space-y-1 pt-1">
                    <div className="text-foreground font-medium flex justify-between">
                      <span>{liveVesselResults.length} vessels in AOI</span>
                      {vesselDataSource && <span className="text-muted-foreground">{vesselDataSource}</span>}
                    </div>
                    {liveVesselResults.slice(0, 8).map((v: any, i: number) => (
                      <div
                        key={i}
                        onClick={() => setSelectedVesselId(v.mmsi)}
                        className={`flex justify-between cursor-pointer rounded px-1 -mx-1 hover:bg-muted/50 ${
                          selectedVesselId === v.mmsi ? 'bg-blue-500/10 text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        <span className="text-blue-400">{(v.name || v.mmsi || '?').trim()}</span>
                        <span>
                          {v.flag || '?'} · {v.type || '?'}
                          {v.destination ? ` · →${v.destination}` : ''}
                        </span>
                      </div>
                    ))}
                    {liveVesselResults.length > 8 && (
                      <div className="text-muted-foreground">+{liveVesselResults.length - 8} more</div>
                    )}
                  </div>
                )}
                {!isFetchingVessels && liveVesselResults.length === 0 && vesselDataSource === '' && (
                  <p className="text-[10px] text-muted-foreground">No vessels currently tracked in this AOI.</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="entities" className="data-[state=active]:flex-1 min-h-0 overflow-y-auto p-4 space-y-4 mt-0">
            <EntityNavigator
              onEntitySelect={handleEntitySelect}
              selectedAOI={selectedAOI}
            />
          </TabsContent>

          <TabsContent value="timeline" className="data-[state=active]:flex-1 min-h-0 mt-0 flex flex-col">
            {/* Scene loader panel */}
            <div className="px-4 pt-4 pb-3 border-b border-border space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-foreground">Satellite Scene Library</p>
                  {selectedAOI ? (
                    <p className="text-xs text-green-400 mt-0.5">AOI: {selectedAOI.name || 'Selected AOI'}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">Select an AOI in Monitor tab first</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    const coords = (() => {
                      if (!selectedAOI?.geometry?.coordinates) return null;
                      const coords = selectedAOI.geometry.coordinates;
                      const ring = (coords[0] as number[][]) || (coords as unknown as number[][]);
                      if (!ring?.length) return null;
                      const lons = ring.map((c: number[]) => c[0]);
                      const lats = ring.map((c: number[]) => c[1]);
                      return [(lons.reduce((a: number, b: number) => a + b, 0) / lons.length), (lats.reduce((a: number, b: number) => a + b, 0) / lats.length)] as [number, number];
                    })();
                    if (coords) {
                      fetchReleasesForLocation(coords, timelineDateRange);
                    } else if (selectedLocation?.coordinates) {
                      fetchReleasesForLocation(selectedLocation.coordinates, timelineDateRange);
                    }
                  }}
                  disabled={loading || (!selectedAOI && !selectedLocation)}
                  className="shrink-0 px-3 py-1.5 text-xs font-medium rounded bg-green-700 hover:bg-green-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                >
                  {loading ? (
                    <><svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Loading...</>
                  ) : 'Load Scenes'}
                </button>
              </div>

              {/* Date range selector */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground shrink-0">Search window:</span>
                <div className="flex gap-1 flex-wrap">
                  {[
                    { label: '1 mo', days: 30 },
                    { label: '3 mo', days: 90 },
                    { label: '6 mo', days: 180 },
                    { label: '1 yr', days: 365 },
                  ].map(({ label, days }) => (
                    <button
                      key={days}
                      onClick={() => setTimelineDateRange(days)}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                        timelineDateRange === days
                          ? 'bg-green-700 border-green-600 text-white'
                          : 'bg-muted border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {releases.length > 0 && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{releases.filter(r => String(r.id).startsWith('eosda_') || String(r.id).startsWith('element84_') || (r.source as string) === 'eosda' || (r.source as string) === 'copernicus').length} Sentinel-2 scenes</span>
                  <span>·</span>
                  <span>{releases.filter(r => !String(r.id).startsWith('eosda_') && !String(r.id).startsWith('element84_') && (r.source as string) !== 'eosda' && (r.source as string) !== 'copernicus').length} Esri Wayback</span>
                  <span className="ml-auto text-green-400">{releases.length} total — select 2+ for MTI</span>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <WaybackTimeline
                releases={releases}
                activeId={activeReleaseId}
                onSelect={handleReleaseSelect}
                onlyLocal={onlyLocal}
                setOnlyLocal={setOnlyLocal}
                loading={false}
                beforeReleaseId={beforeReleaseId}
                afterReleaseId={afterReleaseId}
                onBeforeSelect={(id) => { setBeforeReleaseId(id); if (id) setActiveReleaseId(id); }}
                onAfterSelect={(id) => { setAfterReleaseId(id); if (id) setActiveReleaseId(id); }}
              />
              {!loading && releases.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
                  <svg className="w-10 h-10 text-muted-foreground/30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">No scenes loaded yet</p>
                    <p className="text-xs text-muted-foreground/70">
                      {selectedAOI || selectedLocation
                        ? 'Click "Load Scenes" above to fetch Sentinel-2 imagery for this area'
                        : 'Select an AOI in the Monitor tab, then click "Load Scenes"'}
                    </p>
                  </div>
                  {(selectedAOI || selectedLocation) && (
                    <p className="text-[11px] text-muted-foreground/50 max-w-[220px]">
                      For MTI (Moving Target Indicator) in the Intel tab, you need at least 2 scenes — try a 6-month window
                    </p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="history" className="data-[state=active]:flex-1 min-h-0 overflow-y-auto p-4 mt-0">
            <GEOINTHistory
              onLocationSelect={(coords, name) => {
                setSelectedLocation({ name, coordinates: coords });
                setActiveTab('monitoring');
              }}
            />
          </TabsContent>

          <TabsContent value="intelligence" className="data-[state=active]:flex-1 min-h-0 flex flex-col mt-0">
            {/* Primary: full AOI intel brief (vessels + air + satellite + AI report) */}
            <AOIIntelBrief
              aoi={selectedAOI?.geometry ?? drawnGeometry ?? null}
              onBriefCollected={(brief: any) => {
                setActiveBrief(brief);
                const detections: any[] = brief?.satellite?.detections || [];
                setBriefDetectionPins(
                  detections
                    .filter((d: any) => d.lat && d.lon)
                    .map((d: any, i: number) => ({
                      entity_id: `det_${i}_${d.lat}_${d.lon}`,
                      name: d.adsb_callsign
                        ? `${d.class_name} ${d.adsb_callsign} (${((d.confidence || 0) * 100).toFixed(0)}%)`
                        : `${d.class_name} ${((d.confidence || 0) * 100).toFixed(0)}%`,
                      entity_type: (d.class_name || 'DETECTION').toUpperCase(),
                      lat: d.lat,
                      lon: d.lon,
                      priority: d.transponder_off ? 3 : d.confidence > 0.7 ? 2 : 1,
                    }))
                );
                // Pass tile_results to WaybackMap for bbox rendering
                const tileResults = brief?.satellite?.tile_results;
                if (tileResults?.length) {
                  setBriefSatelliteResults({ tile_results: tileResults });
                }
              }}
            />

            {/* Secondary: advanced satellite-only tools (MTI, focused scan) */}
            <details className="border-t border-slate-700/50 shrink-0">
              <summary className="px-4 py-2 text-xs text-slate-500 cursor-pointer hover:text-slate-300 select-none">
                Advanced Satellite Tools (MTI / Scene Compare)
              </summary>
              <SentinelIntelPanel
                aoi={selectedAOI?.geometry ?? drawnGeometry ?? null}
                releases={releases}
              />
            </details>
          </TabsContent>
        </Tabs>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-card border-b border-border px-3 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            {/* 3D toggle */}
            <button
              onClick={() => setEnable3D(!enable3D)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                enable3D ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              3D Terrain
            </button>

            {/* Intelligence overlay toggles */}
            <div className="h-4 w-px bg-border" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Intel:</span>

            <button
              onClick={() => setShowGlobalBases(!showGlobalBases)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showGlobalBases ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Military Bases
            </button>
            <button
              onClick={() => setShowConflictZones(!showConflictZones)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showConflictZones ? 'bg-red-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Conflict Zones
            </button>
            <button
              onClick={() => setShowNuclearSites(!showNuclearSites)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showNuclearSites ? 'bg-yellow-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Nuclear
            </button>
            <button
              onClick={() => setShowChokepoints(!showChokepoints)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showChokepoints ? 'bg-orange-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Chokepoints
            </button>

            {/* Phase 2: Spectral Index Selector */}
            <div className="h-4 w-px bg-border" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Band:</span>
            <select
              value={spectralIndex || 'rgb'}
              onChange={(e) => setSpectralIndex(e.target.value === 'rgb' ? null : e.target.value)}
              disabled={!selectedAOI && !activeReleaseId}
              title={!selectedAOI && !activeReleaseId ? 'Select an AOI or scene first' : 'Change spectral band'}
              className="bg-muted text-foreground text-xs rounded px-2 py-1 border border-border focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <option value="rgb">RGB (True Color)</option>
              <option value="ndvi">NDVI (Vegetation)</option>
              <option value="ndwi">NDWI (Water)</option>
              <option value="ndbi">NDBI (Built-up)</option>
              <option value="nbr">NBR (Burn Ratio)</option>
            </select>

            {/* Phase 2: SAR toggle */}
            <button
              onClick={() => {
                if (!selectedAOI) { alert('Select an AOI first to use SAR imagery.'); return; }
                setShowSAROnly(!showSAROnly);
              }}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showSAROnly ? 'bg-violet-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              } ${!selectedAOI ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={selectedAOI ? 'Show Sentinel-1 SAR imagery (all-weather radar)' : 'Select an AOI first'}
            >
              SAR
            </button>

            {/* Phase 2: FIRMS thermal overlay */}
            <button
              onClick={() => {
                if (!selectedAOI) { alert('Select an AOI first to use thermal overlay.'); return; }
                setShowFIRMS(!showFIRMS);
              }}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showFIRMS ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              } ${!selectedAOI ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={selectedAOI ? 'NASA FIRMS thermal anomalies (fires, hotspots)' : 'Select an AOI first'}
            >
              Thermal
            </button>

            <div className="h-4 w-px bg-border" />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={showLabels}
                onChange={(e) => setShowLabels(e.target.checked)}
                className="rounded border-border w-3.5 h-3.5"
              />
              Labels
            </label>
          </div>

          <div className="flex items-center space-x-2">
            {/* Right panel toggle */}
            <button
              onClick={() => setShowRightPanel(!showRightPanel)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                showRightPanel ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
              title="Toggle Intel Feed panel"
            >
              Intel Feed
            </button>

            <GEOINTNotifications
              onAOISelect={(aoiId, notificationType) => {
                const aoi = savedAOIs.find(a => a.id === aoiId || a.aoi_id === aoiId);
                if (!aoi) return;
                setSelectedAOI(aoi);
                // "Check Live Aircraft"/"Check Live Vessels" live in the analysis tab.
                setActiveTab('analysis');
                // Auto-populate the rich FR24/VesselAPI view for the AOI that
                // triggered the alert, instead of requiring a second manual click —
                // still one bounded, on-demand query, same as clicking the button.
                if (notificationType === 'aircraft_activity') checkLiveAircraftForAOI(aoi);
                else if (notificationType === 'vessel_activity') checkLiveVesselsForAOI(aoi);
              }}
            />

            <button
              onClick={handleCompareToggle}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {showCompare ? 'Exit Compare' : 'Compare'}
            </button>

            {activeReleaseId && (
              <SnapshotButton
                mapRef={mapRef}
                meta={{
                  releaseId: activeReleaseId,
                  selectedLocation: selectedLocation,
                  center: selectedLocation?.coordinates || [0, 0],
                  zoom: 10
                }}
                onSaved={handleSnapshotSaved}
              />
            )}
          </div>
        </div>

        {/* Map Area */}
        <div className="flex-1 relative" style={{ zIndex: drawingMode ? 10 : 1 }}>
          {showCompare ? (
            <CompareSwipe
              releaseA={compareReleaseA}
              releaseB={compareReleaseB}
              selectedLocation={selectedLocation}
              releases={releases}
              aoi={selectedAOI}
              onReleaseAChange={(id) => {
                setCompareReleaseA(id);
                setBeforeReleaseId(id);
              }}
              onReleaseBChange={(id) => {
                setCompareReleaseB(id);
                setAfterReleaseId(id);
              }}
              onChangeDetectionComplete={(job) => {
                setChangeDetectionJob(job);
                // Auto-switch to analysis tab to show results and enable report
                setActiveTab('analysis');
              }}
            />
          ) : (
            <>
            <WaybackMap
              ref={mapRef}
              activeReleaseId={activeReleaseId}
              selectedLocation={selectedLocation}
              showLabels={showLabels}
              showMilitaryBases={showGlobalBases}
              militaryBases={militaryBases}
              drawingMode={drawingMode}
              drawnAOI={drawnGeometry}
              savedAOIs={savedAOIs}
              selectedAOIId={selectedAOI?.id}
              releases={releases}
              enable3D={enable3D}
              showGlobalBases={showGlobalBases}
              showConflictZones={showConflictZones}
              showNuclearSites={showNuclearSites}
              showChokepoints={showChokepoints}
              spectralIndex={spectralIndex}
              showFIRMS={showFIRMS}
              showSAROnly={showSAROnly}
              objectDetectionResults={briefSatelliteResults || objectDetectionResults}
              changeDetectionJob={changeDetectionJob}
              entityPins={[...entityPins, ...briefDetectionPins]}
              onEntityPinClick={handleEntityPinClick}
              selectedEntityHighlight={selectedEntity && (selectedEntity.lat ?? selectedEntity.attributes?.latitude) ? {
                lat: selectedEntity.lat ?? selectedEntity.attributes?.latitude,
                lon: selectedEntity.lon ?? selectedEntity.attributes?.longitude,
                entity_type: selectedEntity.entity_type || 'ASSET',
                name: selectedEntity.name || '',
              } : null}
              onAOIDraw={(geometry) => {
                setDrawnGeometry(geometry);
              }}
              liveAircraft={showcaseAircraft}
              liveVessels={showcaseVessels}
              onMapReady={loadShowcaseTraffic}
              onLiveAircraftClick={(id) => { setSelectedAircraftId(id); setSelectedVesselId(null); }}
              onLiveVesselClick={(id) => { setSelectedVesselId(id); setSelectedAircraftId(null); }}
              selectedFlightPath={selectedFlightPath}
            />

            {/* Selected aircraft detail card — route + photo (planespotters.net, free/cached) */}
            {selectedAircraft && (
              <div className="absolute top-3 right-3 z-20 w-72 bg-card border border-border rounded-lg shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-orange-500/10">
                  <span className="text-sm font-semibold text-foreground">
                    {(selectedAircraft.callsign || selectedAircraft.icao24 || '?').trim()}
                  </span>
                  <button
                    onClick={() => setSelectedAircraftId(null)}
                    className="text-muted-foreground hover:text-foreground text-xs"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                {isFetchingAircraftPhoto ? (
                  <div className="h-32 flex items-center justify-center text-xs text-muted-foreground">Looking up photo...</div>
                ) : selectedAircraftPhoto?.thumbnail_url ? (
                  <a href={selectedAircraftPhoto.credit_link} target="_blank" rel="noreferrer" title="View on planespotters.net">
                    <img src={selectedAircraftPhoto.thumbnail_url} alt="Aircraft" className="w-full h-36 object-cover" />
                  </a>
                ) : (
                  <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">No photo available</div>
                )}
                {selectedAircraftPhoto?.photographer && (
                  <div className="px-3 pt-1 text-[10px] text-muted-foreground">
                    Photo: {selectedAircraftPhoto.photographer} ·{' '}
                    <a href={selectedAircraftPhoto.credit_link} target="_blank" rel="noreferrer" className="underline hover:text-foreground">
                      planespotters.net
                    </a>
                  </div>
                )}
                <div className="p-3 text-xs space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Type</span>
                    <span className="text-foreground">{selectedAircraft.type || 'Unknown'}</span>
                  </div>
                  {selectedAircraft.registration && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Registration</span>
                      <span className="text-foreground">{selectedAircraft.registration}</span>
                    </div>
                  )}
                  {selectedAircraft.operating_as && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Operator</span>
                      <span className="text-foreground">{selectedAircraft.operating_as}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Route</span>
                    <span className="text-foreground">
                      {selectedAircraft.origin_iata || selectedAircraft.origin_name || '?'} → {selectedAircraft.dest_iata || selectedAircraft.dest_name || '?'}
                    </span>
                  </div>
                  {selectedAircraft.eta && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>ETA</span>
                      <span className="text-foreground">{new Date(selectedAircraft.eta).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Altitude / Speed</span>
                    <span className="text-foreground">
                      {selectedAircraft.on_ground ? 'On ground' : `${selectedAircraft.alt ? Math.round(selectedAircraft.alt) + 'ft' : '?'}`}
                      {' · '}{selectedAircraft.speed ? `${Math.round(selectedAircraft.speed)}kt` : '?'}
                    </span>
                  </div>
                  {!selectedFlightPath && (
                    <p className="text-[10px] text-muted-foreground pt-1">Route not resolvable — origin/destination airport unknown.</p>
                  )}
                </div>
              </div>
            )}

            {/* Selected vessel detail card */}
            {selectedVessel && (
              <div className="absolute top-3 right-3 z-20 w-72 bg-card border border-border rounded-lg shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-blue-500/10">
                  <span className="text-sm font-semibold text-foreground">
                    {(selectedVessel.name || selectedVessel.mmsi || '?').trim()}
                  </span>
                  <button
                    onClick={() => setSelectedVesselId(null)}
                    className="text-muted-foreground hover:text-foreground text-xs"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-3 text-xs space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>MMSI</span>
                    <span className="text-foreground">{selectedVessel.mmsi || '?'}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Type / Flag</span>
                    <span className="text-foreground">{selectedVessel.type || '?'} · {selectedVessel.flag || '?'}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Speed / Course</span>
                    <span className="text-foreground">
                      {selectedVessel.speed != null ? `${selectedVessel.speed.toFixed?.(1) ?? selectedVessel.speed}kt` : '?'}
                      {' · '}{selectedVessel.course != null ? `${Math.round(selectedVessel.course)}°` : '?'}
                    </span>
                  </div>
                  {selectedVessel.destination && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Destination</span>
                      <span className="text-foreground">{selectedVessel.destination}</span>
                    </div>
                  )}
                  {selectedVessel.eta && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>ETA</span>
                      <span className="text-foreground">{selectedVessel.eta}</span>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground pt-1">
                    Vessel photos aren't reliably available yet — heading arrow shown on map instead.
                  </p>
                </div>
              </div>
            )}
            </>
          )}
        </div>

        {/* Attribution */}
        <div className="bg-muted px-4 py-2 text-xs text-muted-foreground flex justify-between">
          <span>Sycek Intelligence Geoint Platform</span>
          <span>Powered by Sycek Intelligence</span>
        </div>
      </div>

      {/* Right Panel - Intel Feed (Phase 4A) */}
      {showRightPanel && (
        <div className="w-80 shrink-0">
          <IntelFeedPanel
            selectedAOI={selectedAOI}
            onEntitySelect={handleEntitySelect}
            onCollapse={() => setShowRightPanel(false)}
          />
        </div>
      )}

      {/* Entity Detail Drawer (Phase 4A) */}
      <EntityDetailDrawer
        entity={selectedEntity}
        open={entityDrawerOpen}
        onClose={() => setEntityDrawerOpen(false)}
        activeBrief={activeBrief}
        onEntitySelect={(entity) => {
          setSelectedEntity(entity);
        }}
        onGeneratePackage={(entityId) => {
          // entityId is passed from the drawer; TargetPackageBuilder reads selectedEntity directly
          void entityId;
          setShowTargetPackageBuilder(true);
        }}
        onNavigateToLocation={(lat, lon, _label) => {
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [lon, lat],
              zoom: 15,
              pitch: 45,
              bearing: -20,
              duration: 2000,
            });
          }
        }}
      />

      {/* Target Package Builder (Phase 4B) */}
      <TargetPackageBuilder
        entityId={selectedEntity?.entity_id}
        aoiId={selectedAOI?.id}
        entityName={selectedEntity?.name || selectedAOI?.name}
        open={showTargetPackageBuilder}
        onClose={() => setShowTargetPackageBuilder(false)}
      />
    </div>
  );
};

export default GEOINT_Wayback;
